import React, { useEffect, useState, useCallback } from "react";
import {
  Grid,
  Card,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Backdrop,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";

function PrivacyPolicy() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState({ name_en: "", name_fr: "", description_en: "", description_fr: "", role: "" });
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [currentId, setCurrentId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: "privacy-policy" };
      const response = await apiRequest.get(endPoints.GET_PAGES, params);
      if (response.success) {
        setPolicies(response.data.items);
      }
    } catch (error) {
      console.error("Error fetching pages:", error);
      setError("Failed to fetch pages. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenEdit = (policy) => {
    setCurrentId(policy.id);
    setEditData({
      name_en: policy.name_en,
      name_fr: policy.name_fr,
      description_en: policy.description_en,
      description_fr: policy.description_fr,
      role: policy.user_type,
    });
    setEditModalOpen(true);
  };

  const handleCloseEdit = () => setEditModalOpen(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const data = {
        name_en: editData.name_en,
        name_fr: editData.name_fr,
        description_en: editData.description_en,
        description_fr: editData.description_fr,
      };
      const response = await apiRequest.put(`${endPoints.UPDATE_PAGE}/${currentId}`, data);
      if (response.success) {
        setSuccess("Policy updated successfully.");
        fetchData();
        handleCloseEdit();
      } else {
        setError(response.message || "Failed to update policy.");
      }
    } catch (error) {
      console.error("Error:", error);
      setError("An error occurred while processing your request.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <MDBox mb={3}>
              <Card>
                <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                  <MDTypography variant="h6" color="white">
                    Privacy Policy
                  </MDTypography>
                </MDBox>
                <MDBox p={3}>
                  {policies.map((policy) => (
                    <Accordion key={policy.id} sx={{ mb: 2, boxShadow: 3 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Box>
                            <MDTypography variant="h6">
                              {policy.name_en} - {policy.name_fr}
                            </MDTypography>
                            <MDTypography variant="caption" color="textSecondary">
                              ({policy.user_type})
                            </MDTypography>
                          </Box>
                          <Button variant="contained" color="primary" style={{ color : "#fff"}} onClick={() => handleOpenEdit(policy)}>
                            Edit
                          </Button>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Card sx={{ mb: 3, p: 2, boxShadow: 3 }}>
                          <MDTypography variant="h6" color="info">
                            English Version
                          </MDTypography>
                          <Divider sx={{ my: 1 }} />
                          <Box dangerouslySetInnerHTML={{ __html: policy.description_en }} sx={{ fontSize: "1rem", lineHeight: "1.6" }} />
                        </Card>
                        <Card sx={{ p: 2, boxShadow: 3 }}>
                          <MDTypography variant="h6" color="info">
                            Arabic Version
                          </MDTypography>
                          <Divider sx={{ my: 1 }} />
                          <Box dangerouslySetInnerHTML={{ __html: policy.description_fr }} sx={{ fontSize: "1rem", lineHeight: "1.6" }} />
                        </Card>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </MDBox>
              </Card>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Backdrop open={loading} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar open={!!error || !!success} autoHideDuration={3000} onClose={() => { setError(null); setSuccess(null); }} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => { setError(null); setSuccess(null); }} severity={error ? "error" : "success"} sx={{ width: "100%" }}>
          {error || success}
        </Alert>
      </Snackbar>

      <Dialog open={editModalOpen} onClose={handleCloseEdit} fullWidth maxWidth="md">
        <DialogTitle>Edit Privacy Policy ({editData.role})</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Title (English)" fullWidth value={editData.name_en} onChange={(e) => setEditData({ ...editData, name_en: e.target.value })} margin="dense" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Title (Arabic)" fullWidth value={editData.name_fr} onChange={(e) => setEditData({ ...editData, name_fr: e.target.value })} margin="dense" />
            </Grid>
            <Grid item xs={12}>
              <MDTypography variant="subtitle1" gutterBottom>
                Description (English)
              </MDTypography>
              <CKEditor editor={ClassicEditor} data={editData.description_en} onChange={(event, editor) => setEditData((prev) => ({ ...prev, description_en: editor.getData() }))} />
            </Grid>
            <Grid item xs={12}>
              <MDTypography variant="subtitle1" gutterBottom>
                Description (Arabic)
              </MDTypography>
              <CKEditor editor={ClassicEditor} data={editData.description_fr} onChange={(event, editor) => setEditData((prev) => ({ ...prev, description_fr: editor.getData() }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSave} style={{ whiteSpace: "nowrap", color: "white" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default PrivacyPolicy;
